import { useDebouncedCallback, useDebouncedValue } from '@mantine/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SetURLSearchParams, useSearchParams } from 'react-router-dom';
import { hasLength } from 'utility/functions';

// ------------------------------------------------
// Base Hook - useStateWithDebouncedCallback
// ------------------------------------------------

interface useStateWithDebouncedCallbackProps<T = any> {
  initialValue: T;
  debounceTime?: number;
  callback: (value: T) => void;
}

/**
 * Returns similar destructured array as useState.
 *
 * Custom React hook that manages a debounced state and triggers a callback with the debounced value.
 * This can be useful e.g. for inputs that automatically search for results after a delay who would like to
 * make a callback with the value debounced after a certain time.
 *
 * @example
 * ```tsx
 * const SearchComponent = () => {
 *   const [searchInput, setSearchInput] = useStateWithDebouncedCallback({
 *     initialValue: '',
 *     debounceTime: 300,
 *     callback: (debouncedValue) => {
 *       console.debug('Searched for:', debouncedValue);
 *     }
 *   });
 *
 *   return (
 *     <div>
 *       <input
 *         type="text"
 *         value={searchInput}
 *         onChange={(e) => setSearchInput(e.target.value)}
 *         placeholder="Type to search..."
 *       />
 *       <p>Current input: {searchInput}</p>
 *     </div>
 *   );
 * };
 * ```
 *
 * @param initialValue - Initial value of the state, can be of any type.
 * @param debounceTime - Delay in milliseconds before the callback is executed after the last state change.
 * @param callback - Function called with the debounced state value.
 */

export function useStateWithDebouncedCallback<T>({
  initialValue,
  debounceTime = 300,
  callback,
}: useStateWithDebouncedCallbackProps<T>) {
  const [value, setValue] = useState<T>(initialValue);

  const debouncedUpdate = useDebouncedCallback((newValue: T) => {
    callback(newValue);
  }, debounceTime);

  const updateValue = (newValue: T) => {
    setValue(newValue);
    debouncedUpdate(newValue);
  };

  return [value, updateValue] as const;
}

/**
 * Update a URL search parameter by key and value.
 * key - The key of the query parameter to be updated.
 * value - The new value for the query parameter, string or an array of strings.
 * setQueryParams - Function to update URLSearchParams, eg via useSearchParams()
 * arrayFormat? - (optional) How to serialize arrays.
 * 'comma' (default) - joins with comma, most commonly used across app.
 * 'repeat' (edge cases) - repeats qp keys, in case values contain delimiter
 *  -- if using 'repeat' format, parse with `qp.getAll('key')`
 *
 * NOTE: Intentionally mutating prevParams rather than copying it.
 * eg: NOT doing `const next = new URLSearchParams(prevParams)`
 * React Router passes the same URLSearchParams object reference around
 * to every synchronous setSearchParams(fn) call in the same render cycle.
 * Copying via new URLSearchParams(...) causes consecutive calls to start
 * from a stale snapshot and last call wins. This occurs in some areas
 * such as paired [min]/[max] date range updateValue handlers, causing
 * the second call to overwrite the first with previous state.
 * Mutation allows calls to build/chain on the previous call changes.
 *
 */

export type QPArrayFormat = 'comma' | 'repeat';

type QueryParamUpdaterFn = (
  key: string,
  value: string | string[],
  setQueryParams: SetURLSearchParams,
  arrayFormat?: QPArrayFormat,
) => void;

export const qpUpdateHelper: QueryParamUpdaterFn = (
  key,
  value,
  setQueryParams,
  arrayFormat = 'comma',
) => {
  setQueryParams((prevParams) => {
    // Clear existing values for this key
    prevParams.delete(key);

    // No value - key already deleted
    if (!value || !hasLength(value)) {
      return prevParams;
    }

    // Handle single / string values
    if (typeof value === 'string') {
      prevParams.set(key, value);
      return prevParams;
    }

    // Handle array values - repeat vs comma format
    if (Array.isArray(value)) {
      if (arrayFormat === 'repeat') {
        // Repeat query param format (edge cases)
        // eg: /?key=value1&key=value2 - (values may contain commas, etc.)
        // parse: qp.getAll('key') - see: getKnownSearchQueryParams
        value.forEach((item) => {
          if (item) {
            prevParams.append(key, item);
          }
        });
      } else {
        // Default (common) - comma joined under one qp key
        // eg: /?key=value1,value2 - (values may not contain delimiter)
        // parse: qp.get('key').split(',')
        prevParams.set(key, value.join(','));
      }
      return prevParams;
    }

    // No case matched, log warning.
    console.warn('[qpUpdateHelper] unhandled kv:', key, value);
    return prevParams;
  });
};

/**
 * ------------------------------------------------
 * useStateWithDebouncedQueryParams
 * ------------------------------------------------
 *
 * A utility hook that updates a query parameter in the URL, debounced.
 * The internal state is not debounced, for for fast UI updates
 * however the query parameter is debounced for URL updates.
 *
 * @param qpkey - The key of the query parameter to be updated.
 * @param initialValue - The initial string value for the hook/qp.
 * @param debounceTime - Milliseconds, default is 350ms.
 *
 * @returns Destructurable array: `[state, setState, currentQueryParam, ...]`
 *
 * @example
 * ```tsx
 * import React from 'react';
 * import { useStateWithDebouncedQueryParams } from './useStateWithDebouncedQueryParams';
 *
 * const SearchComponent = () => {
 *   const [textFieldState, setTextFieldState, currentQP] = useStateWithDebouncedQueryParams({
 *      qpkey: 'query',
 *      initialValue: queryParams.get('query') || '',
 *   });
 *
 *   return (
 *     <div>
 *       <input
 *         type="text"
 *         value={searchInput}
 *         onChange={(e) => setSearchInput(e.target.value)}
 *         placeholder="Type to search..."
 *       />
 *       <p>Current search query in URL: {currentQP || 'None'}</p>
 *     </div>
 *   );
 * };
 *
 * // eg: some other function updates the qp elsewhere
 * // test: external updates syncing back to state
 *   <button
 *     onClick={() => {
 *       const randomText = Math.random().toString(36).substring(2, 7);
 *       updateQueryParams((prev) => {
 *         const newParams = new URLSearchParams(prev);
 *         newParams.set('query', randomText);
 *         return newParams;
 *       });
 *     }}
 *   >
 *     Set Random QP
 *   </button>
 * ```
 */

type UseStateWithDebouncedQueryParamsProps = {
  qpkey: string;
  initialValue: string;
  debounceTime?: number;
  disabled?: boolean;
};

export function useStateWithDebouncedQueryParams({
  qpkey,
  initialValue = '',
  debounceTime = 350,
  disabled = false,
}: UseStateWithDebouncedQueryParamsProps) {
  const [queryParams, setQueryParams] = useSearchParams();

  // Set initial value - effect syncs if qp differs
  const [value, setValue] = useState<string>(initialValue);

  // Debounced state for sync effect
  const [dbValue, cancel] = useDebouncedValue(value, debounceTime);

  // Tracks the last value we set to qp
  // External change if differs from what we last set
  const lastSetRef = useRef<string>(initialValue);

  const handleChange = useCallback((next: string) => {
    setValue(next);
  }, []);

  // Watch qp value for external changes
  const qpValueStr = queryParams.get(qpkey) ?? '';

  // QP Sync Effect
  // - sync debounced value out to QP
  // - external QP changes in to state asap
  useEffect(() => {
    if (disabled) {
      return;
    }

    // External QP change
    // - set state immediately, cancel db value updates
    // - sync qp value to ref and local state
    if (qpValueStr !== lastSetRef.current) {
      cancel();
      lastSetRef.current = qpValueStr;
      setValue(qpValueStr);
      return;
    }

    // Debounced value changed
    // - update qp and track via ref
    lastSetRef.current = dbValue;
    qpUpdateHelper(qpkey, dbValue, setQueryParams);
  }, [qpValueStr, qpkey, dbValue, disabled, cancel, setQueryParams]);

  return [value, handleChange, queryParams.get(qpkey), disabled] as const;
}
